/**
 * JSON-RPC provider policy. Only keyed, paid providers are allowed: Alchemy and keyed dRPC.
 * Keyless public endpoints (thirdweb, publicnode, public dRPC, ethpandaops, viem chain
 * defaults, ...) are rate limited per caller and fail under load, so no code path may use one.
 * `scripts/check-rpc-providers.ts` enforces the source side; hosted configuration parsers call
 * {@link assertPaidRpcUrl}.
 */

/** Alchemy network slug per chain id. */
export const ALCHEMY_NETWORKS = {
  1: "eth-mainnet",
  11155111: "eth-sepolia",
  2741: "abstract-mainnet",
  8453: "base-mainnet",
  42161: "arb-mainnet",
} as const satisfies Record<number, string>;

export type AlchemyChainId = keyof typeof ALCHEMY_NETWORKS;

const ALCHEMY_KEY = /^[A-Za-z0-9_-]{16,64}$/;
const ALCHEMY_HOST = /^[a-z0-9-]+\.g\.alchemy\.com$/;
const ALCHEMY_PATH = /^\/v2\/[A-Za-z0-9_-]{16,64}$/;
const DRPC_LIVE_PATH = /^\/[a-z0-9-]+\/[A-Za-z0-9_-]{16,}$/;

/** Required Alchemy API key; the message names the variable, never the value. */
export function parseAlchemyApiKey(value: string | undefined, name: string): string {
  const key = value?.trim() ?? "";
  if (!ALCHEMY_KEY.test(key)) throw new Error(`${name} must be an Alchemy API key; public RPC fallback is disabled`);
  return key;
}

export function alchemyRpcUrl(chainId: AlchemyChainId, apiKey: string): string {
  return `https://${ALCHEMY_NETWORKS[chainId]}.g.alchemy.com/v2/${apiKey}`;
}

/**
 * Browsers hold no RPC key: every Squeekchain browser reads through the `apps/rpc-proxy` Worker,
 * which checks origin, method and size, rate-limits per client and adds its own Alchemy key.
 */
export const BROWSER_RPC_ORIGIN = "https://rpc.squeekchain.com";
export type BrowserRpcChainId = 1 | 11155111 | 2741;

export function browserRpcUrl(chainId: BrowserRpcChainId): string {
  return `${BROWSER_RPC_ORIGIN}/v1/${chainId}`;
}

/** True only for a keyed Alchemy or dRPC endpoint over TLS without embedded credentials. */
export function isPaidRpcUrl(value: string | URL): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if ((url.protocol !== "https:" && url.protocol !== "wss:") || url.username !== "" || url.password !== "" || url.hash !== "") return false;
  if (ALCHEMY_HOST.test(url.hostname)) return ALCHEMY_PATH.test(url.pathname) && url.search === "";
  if (url.hostname === "lb.drpc.live") return DRPC_LIVE_PATH.test(url.pathname) && url.search === "";
  if (url.hostname === "lb.drpc.org") {
    return url.pathname === "/ogrpc" && (url.searchParams.get("dkey") ?? "") !== "" && (url.searchParams.get("network") ?? "") !== "";
  }
  return false;
}

/** Returns the URL unchanged; the error names the variable and never echoes the (keyed) value. */
export function assertPaidRpcUrl(value: string, name: string): string {
  if (!isPaidRpcUrl(value)) {
    throw new Error(`${name} must be a keyed Alchemy or dRPC endpoint; public RPC endpoints are not allowed`);
  }
  return value;
}
